import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { tileWithNeighbors } from '../utils/slippy-tile';

const SIDECAR_ZOOM = 16;

/** One matched unit's full address, as written by generate-registry-tiles.mts. */
export interface RegistryUnit {
  id: string;
  building_id: string;
  unit: string;
  formatted_address: string;
  house_number: string;
  street: string;
  city: string;
  region: string;
  postal_code: string;
  country: string;
  place_type: string;
  lat: number;
  lng: number;
}

/** One sidecar shard file's contents: building id -> its matched units. */
type SidecarShard = Record<string, RegistryUnit[]>;

/**
 * Fetches the address details a map tile's `buildings` layer deliberately doesn't carry
 * (see generate-tiles.ts for why) from the sidecar JSON files it emits alongside the
 * .pmtiles archive, served by the backend's registry static route (routes/registry.ts) —
 * same host-derivation convention as the pmtiles source URL in map.component.ts.
 */
@Injectable({
  providedIn: 'root',
})
export class RegistryLookupService {
  /** Shard path ('region/zoom/x/y') -> its parsed contents, or null for a confirmed 404. */
  private cache = new Map<string, SidecarShard | null>();

  /**
   * The matched units for one building, given the location it was tapped at. Checks the
   * tapped tile first, then its 8 neighbours (a tap near a polygon edge can land one tile
   * away from the shard the building's own centroid was filed under — see slippy-tile.ts).
   * Returns an empty array if nothing is found anywhere nearby, never throws for that case.
   */
  async lookupUnits(region: string, buildingId: string, lng: number, lat: number): Promise<RegistryUnit[]> {
    for (const { x, y } of tileWithNeighbors(lng, lat, SIDECAR_ZOOM)) {
      const shard = await this.getShard(region, x, y);
      const units = shard?.[buildingId];
      if (units) return units;
    }
    return [];
  }

  private async getShard(region: string, x: number, y: number): Promise<SidecarShard | null> {
    const key = `${region}/${SIDECAR_ZOOM}/${x}/${y}`;
    if (this.cache.has(key)) return this.cache.get(key)!;

    let shard: SidecarShard | null;
    try {
      const res = await fetch(`${environment.apiUrl.replace('/api', '')}/registry/${key}.json`);
      shard = res.ok ? ((await res.json()) as SidecarShard) : null;
    } catch {
      shard = null; // offline or network error -- treat like "not found", don't throw
    }

    this.cache.set(key, shard);
    return shard;
  }
}
